import { formatDateKey } from "src/utils/string/stringUtil";
import {
	buildDateKey,
	extractDayDdFromDateKey,
	getDaysInMonth,
} from "src/utils/date/dateUtils";
import {
	defaultEQM2070_R01DetailDto,
	EQM2070_R01DetailDto,
	EQM2070_R01DetailPivotDto,
	EQM2070_R01InspectMasterDto,
	EQM2070_R01OxCellValue,
} from "./Dto_EQM2070_R01";

export function normalizeOxCellValue(value: unknown): EQM2070_R01OxCellValue {
	const normalized = String(value ?? "")
		.trim()
		.toUpperCase();
	if (normalized === "O") return "O";
	if (normalized === "X") return "X";
	return "";
}

/** 점검코드+일자로 flat형 상세 Dto 비교 키 생성 */
function buildDetailKey(inspectCd: string, inspectDt: Date | string | undefined): string {
	return `${inspectCd}_${formatDateKey(inspectDt)}`;
}

/** 점검년월 기준 2자리 일(dd) 목록 */
function getInspectDayDdList(inspectDtYm: string): string[] {
	const days = getDaysInMonth(inspectDtYm);
	return Array.from({ length: days }, (_, index) => String(index + 1).padStart(2, "0"));
}

/** pivot 행의 빈 일자별 O/X 값 맵 생성 (키: 2자리 일) */
function createEmptyDayValues_EQM2070_R01(inspectDtYm: string): Record<string, EQM2070_R01OxCellValue> {
	return Object.fromEntries(
		getInspectDayDdList(inspectDtYm).map((dayDd) => [dayDd, ""]),
	) as Record<string, EQM2070_R01OxCellValue>;
}

/** flat형 상세 Dto DTO에서 inspect master 필드 추출 */
function toInspectMasterFromFlat(item: EQM2070_R01DetailDto): EQM2070_R01InspectMasterDto {
	return {
		eqmType: item.eqmType || "",
		eqmTypeNm: item.eqmTypeNm || "",
		inspectCd: item.inspectCd,
		inspectNm: item.inspectNm,
	};
}

/** flat형 상세 Dto + 점검기준을 pivot 그리드 행으로 변환 */
export function pivotDetailToGridRows(
	flatList: EQM2070_R01DetailDto[],
	inspectMasterList: EQM2070_R01InspectMasterDto[],
	inspectDtYm: string,
): EQM2070_R01DetailPivotDto[] {
	const rowSource: EQM2070_R01InspectMasterDto[] =
		inspectMasterList.length > 0
			? inspectMasterList
			: Array.from(
					new Map(
						flatList.map((item) => [item.inspectCd, toInspectMasterFromFlat(item)]),
					).values(),
				);
	return rowSource.map((inspect, index) => {
		const dayValues = createEmptyDayValues_EQM2070_R01(inspectDtYm);
		const dayMeta: Record<string, { itemNo?: string }> = {};
		flatList
			.filter((item) => item.inspectCd === inspect.inspectCd)
			.forEach((item) => {
				const dateKey = formatDateKey(item.inspectDt);
				if (!dateKey.startsWith(inspectDtYm)) return;
				const dayDd = extractDayDdFromDateKey(dateKey);
				dayValues[dayDd] = normalizeOxCellValue(item.inspectValue);
				if (item.itemNo) {
					dayMeta[dayDd] = { itemNo: item.itemNo };
				}
			});
		return {
			valueIndex: index + 1,
			eqmType: inspect.eqmType,
			eqmTypeNm: inspect.eqmTypeNm || "",
			inspectCd: inspect.inspectCd,
			inspectNm: inspect.inspectNm,
			dayValues,
			dayMeta,
			isModified: false,
		};
	});
}

/** pivot 그리드 행을 저장용 flat형 상세 Dto 배열로 변환 */
export function flattenGridRowsToDetail(
	pivotList: EQM2070_R01DetailPivotDto[],
	base: Pick<
		EQM2070_R01DetailDto,
		"inspectNo" | "compCd" | "equipmentCd" | "inspectCycle" | "judgeEmpno" | "judgeEmpNm"
	>,
	inspectDtYm: string,
): EQM2070_R01DetailDto[] {
	const result: EQM2070_R01DetailDto[] = [];
	const inspectDayDdList = getInspectDayDdList(inspectDtYm);
	pivotList.forEach((row) => {
		inspectDayDdList.forEach((dayDd) => {
			const value = row.dayValues[dayDd];
			if (value !== "O" && value !== "X") return;
			const dateKey = buildDateKey(inspectDtYm, dayDd);
			result.push({
				...defaultEQM2070_R01DetailDto,
				inspectNo: base.inspectNo,
				compCd: base.compCd,
				equipmentCd: base.equipmentCd,
				inspectCycle: base.inspectCycle,
				judgeEmpno: base.judgeEmpno,
				judgeEmpNm: base.judgeEmpNm,
				inspectCd: row.inspectCd,
				inspectNm: row.inspectNm,
				inspectDt: dateKey,
				inspectValue: value,
				itemNo: row.dayMeta[dayDd]?.itemNo || "",
				inserted: !row.dayMeta[dayDd]?.itemNo,
				isModified: false,
			});
		});
	});
	return result;
}

/** 원본/현재 flat형 상세 Dto를 비교해 추가·수정·삭제 대상 추출 */
export function diffFlatDetailLists(
	original: EQM2070_R01DetailDto[],
	current: EQM2070_R01DetailDto[],
	inspectDtYm: string,
): EQM2070_R01DetailDto[] {
	const filterByMonth = (list: EQM2070_R01DetailDto[]) =>
		list.filter((item) => formatDateKey(item.inspectDt).startsWith(inspectDtYm));
	const originalFiltered = filterByMonth(original);
	const currentFiltered = filterByMonth(current);
	const originalMap = new Map(
		originalFiltered.map((item) => [buildDetailKey(item.inspectCd, item.inspectDt), item]),
	);
	const currentMap = new Map(
		currentFiltered.map((item) => [buildDetailKey(item.inspectCd, item.inspectDt), item]),
	);
	const inserted = currentFiltered.filter(
		(item) => !originalMap.has(buildDetailKey(item.inspectCd, item.inspectDt)),
	);
	const modified = currentFiltered
		.filter((item) => {
			const originalItem = originalMap.get(buildDetailKey(item.inspectCd, item.inspectDt));
			if (!originalItem) return false;
			return (
				normalizeOxCellValue(originalItem.inspectValue) !==
				normalizeOxCellValue(item.inspectValue)
			);
		})
		.map((item) => ({
			...item,
			itemNo: originalMap.get(buildDetailKey(item.inspectCd, item.inspectDt))?.itemNo || item.itemNo,
			isModified: true,
			inserted: false,
		}));
	const deleted = originalFiltered
		.filter((item) => !currentMap.has(buildDetailKey(item.inspectCd, item.inspectDt)))
		.map((item) => ({
			...item,
			inspectValue: "",
			isModified: true,
			inserted: false,
		}));
	return [...inserted, ...modified, ...deleted];
}
